import { BACKEND_URL } from '../utils/config';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import apiClient from '../services/api';
import { 
  ArrowLeft, Save, Loader2, AlertCircle, CheckCircle2,
  Image as ImageIcon, Video, Upload, RotateCcw, Users, Trash2
} from 'lucide-react';
import { Switch } from '../components/ui/switch';

const EditCampaignPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { t } = useLanguage();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [campaign, setCampaign] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    story: '',
    goal: '',
    image: '',
    video_url: '',
    team_fundraising_enabled: false
  });
  const [togglingTeam, setTogglingTeam] = useState(false);
  const [deletingImage, setDeletingImage] = useState(false);

  // Handle image deletion
  const handleDeleteImage = async () => {
    if (!formData.image) return;
    
    // Extract the key from the image URL
    const imageUrl = formData.image;
    let key = '';
    
    try {
      // Handle different URL formats
      if (imageUrl.includes('.r2.dev/') || imageUrl.includes('r2.cloudflarestorage')) {
        const urlParts = new URL(imageUrl);
        key = urlParts.pathname.substring(1); // Remove leading /
      } else if (imageUrl.startsWith('/uploads/')) {
        key = imageUrl.substring(1); // Remove leading /
      } else {
        // For other URLs, just clear the image locally
        setFormData(prev => ({ ...prev, image: '' }));
        setSuccess('Image cleared. Save changes to update the campaign.');
        setTimeout(() => setSuccess(''), 3000);
        return;
      }
      
      if (key) {
        setDeletingImage(true);
        const response = await apiClient.delete(`/r2/delete/${encodeURIComponent(key)}`);
        if (response.data.success) {
          setFormData(prev => ({ ...prev, image: '' }));
          setSuccess('Image deleted successfully. Save changes to update the campaign.');
          setTimeout(() => setSuccess(''), 3000);
        }
      }
    } catch (err) {
      console.error('Failed to delete image:', err);
      // Still clear the image locally even if server deletion fails
      setFormData(prev => ({ ...prev, image: '' }));
      setSuccess('Image removed. Save changes to update the campaign.');
      setTimeout(() => setSuccess(''), 3000);
    } finally {
      setDeletingImage(false);
    }
  };

  useEffect(() => {
    const fetchCampaign = async () => {
      try {
        const response = await apiClient.get(`/campaigns/${id}`);
        const c = response.data;
        setCampaign(c);
        setFormData({
          title: c.title || '',
          story: c.story || '',
          goal: c.goal || '',
          image: c.image || '',
          video_url: c.video_url || '',
          team_fundraising_enabled: c.team_fundraising_enabled || false
        });
        setError('');
      } catch (err) {
        console.error('Error loading campaign:', err);
        const status = err.response?.status;
        const detail = err.response?.data?.detail;
        
        if (status === 404) {
          // Check if user might need to re-authenticate
          const token = localStorage.getItem('token');
          if (!token) {
            setError('Please log in to edit this campaign');
          } else {
            setError('Campaign not found or you do not have permission to edit it. Please try refreshing the page.');
          }
        } else if (status === 401) {
          setError('Your session has expired. Please log in again.');
          // Clear token and redirect to login
          localStorage.removeItem('token');
          setTimeout(() => {
            window.location.href = '/login?redirect=' + encodeURIComponent(window.location.pathname);
          }, 2000);
        } else {
          setError(detail || 'Failed to load campaign. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchCampaign();
    }
  }, [id]);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        
        const response = await fetch(`${BACKEND_URL}/api/upload/image`, {
          method: 'POST',
          body: formDataUpload
        });
        
        const result = await response.json();
        if (result.success) {
          const imageUrl = `${BACKEND_URL}${result.url}`;
          setFormData(prev => ({ ...prev, image: imageUrl }));
        }
      } catch (err) {
        console.error('Upload error:', err);
      }
    }
  };

  const handleToggleTeamFundraising = async () => {
    setTogglingTeam(true);
    try {
      const response = await apiClient.post(`/campaigns/${id}/team-fundraising/toggle`);
      setFormData(prev => ({ 
        ...prev, 
        team_fundraising_enabled: response.data.team_fundraising_enabled 
      }));
      setSuccess(response.data.team_fundraising_enabled 
        ? 'Team fundraising enabled! Supporters can now create fundraising pages for your campaign.'
        : 'Team fundraising disabled.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to toggle team fundraising');
    } finally {
      setTogglingTeam(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    try {
      const updateData = {};
      if (formData.title !== campaign.title) updateData.title = formData.title;
      if (formData.story !== campaign.story) updateData.story = formData.story;
      if (formData.goal !== campaign.goal) updateData.goal = parseFloat(formData.goal);
      if (formData.image !== campaign.image) updateData.image = formData.image;
      if (formData.video_url !== campaign.video_url) updateData.video_url = formData.video_url;
      // Note: team_fundraising_enabled is saved via toggle, but include if changed
      if (formData.team_fundraising_enabled !== (campaign.team_fundraising_enabled || false)) {
        updateData.team_fundraising_enabled = formData.team_fundraising_enabled;
      }

      if (Object.keys(updateData).length === 0) {
        setSuccess('All changes are already saved!');
        setSaving(false);
        setTimeout(() => setSuccess(''), 2000);
        return;
      }

      const response = await apiClient.put(`/campaigns/${id}/edit`, updateData);
      
      // Show appropriate message based on whether review is required
      if (response.data.requires_review) {
        setSuccess('Campaign updated! Your changes are pending admin review. The campaign will be visible again once approved.');
      } else {
        setSuccess('Campaign updated successfully!');
      }
      
      // Redirect back to dashboard after short delay
      setTimeout(() => {
        navigate('/dashboard');
      }, 2500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update campaign');
    } finally {
      setSaving(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <Card className="max-w-md">
            <CardContent className="pt-6 text-center">
              <AlertCircle className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
              <p className="text-foreground mb-4">{t('pleaseLoginToEdit')}</p>
              <Link to="/login">
                <Button className="bg-gradient-to-r from-emerald-500 to-teal-600">{t('signIn')}</Button>
              </Link>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </main>
        <Footer />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 flex items-center justify-center p-4">
          <Card className="max-w-md w-full">
            <CardContent className="pt-6 text-center">
              <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
              <h2 className="text-lg font-semibold mb-2">{t('campaignNotFound')}</h2>
              <p className="text-muted-foreground mb-6">
                {error || 'This campaign could not be loaded. It may have been deleted, or you may not have permission to edit it.'}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button 
                  variant="outline" 
                  onClick={() => window.location.reload()}
                  className="gap-2"
                >
                  <RotateCcw className="w-4 h-4" />
                  Try Again
                </Button>
                <Link to="/dashboard">
                  <Button className="w-full sm:w-auto bg-gradient-to-r from-emerald-500 to-teal-600">
                    {t('backToDashboard')}
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  // Check if user owns this campaign
  if (campaign.creator?.id !== user?.id) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <Card className="max-w-md">
            <CardContent className="pt-6 text-center">
              <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
              <p className="text-foreground mb-4">{t('noPermissionToEdit')}</p>
              <Link to="/dashboard">
                <Button variant="outline">{t('backToDashboard')}</Button>
              </Link>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1 py-8">
        <div className="max-w-3xl mx-auto px-4">
          {/* Back Button */}
          <Link to={`/campaign/${id}`} className="inline-flex items-center text-muted-foreground hover:text-foreground mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" /> {t('backToCampaign')}
          </Link>

          <Card>
            <CardHeader>
              <CardTitle className="text-2xl">{t('editCampaign')}</CardTitle>
              <CardDescription>{t('updateYourCampaign')}</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                {success && (
                  <Alert className="bg-emerald-500/10 border-emerald-500/30">
                    <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                    <AlertDescription className="text-emerald-400">{success}</AlertDescription>
                  </Alert>
                )}

                {/* Title */}
                <div>
                  <Label htmlFor="title">{t('campaignTitleLabel')}</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    className="mt-2 bg-secondary"
                  />
                </div>

                {/* Goal */}
                <div>
                  <Label htmlFor="goal">{t('fundraisingGoal')}</Label>
                  <Input
                    id="goal"
                    type="number"
                    value={formData.goal}
                    onChange={(e) => setFormData(prev => ({ ...prev, goal: e.target.value }))}
                    className="mt-2 bg-secondary"
                  />
                </div>

                {/* Story */}
                <div>
                  <Label htmlFor="story">{t('yourStory')}</Label>
                  <Textarea
                    id="story"
                    value={formData.story}
                    onChange={(e) => setFormData(prev => ({ ...prev, story: e.target.value }))}
                    className="mt-2 min-h-[200px] bg-secondary"
                  />
                </div>

                {/* Image */}
                <div>
                  <Label>{t('campaignImage')}</Label>
                  <input
                    type="file"
                    id="imageInput"
                    className="hidden"
                    accept="image/*"
                    onChange={handleImageUpload}
                  />
                  <div 
                    className="mt-2 border-2 border-dashed border-border rounded-xl p-4 text-center hover:border-emerald-500/50 transition-colors cursor-pointer bg-secondary/50"
                    onClick={() => document.getElementById('imageInput').click()}
                  >
                    {formData.image ? (
                      <div className="relative">
                        <img 
                          src={formData.image} 
                          alt="Campaign" 
                          className="w-full h-48 object-cover rounded-lg"
                        />
                        <div className="flex gap-2 mt-2 justify-center">
                          <Button 
                            type="button"
                            variant="outline" 
                            onClick={(e) => {
                              e.stopPropagation();
                              document.getElementById('imageInput').click();
                            }}
                          >
                            <Upload className="w-4 h-4 mr-2" />
                            {t('editImage')}
                          </Button>
                          <Button 
                            type="button"
                            variant="outline"
                            className="text-red-400 hover:bg-red-500/10 hover:text-red-400"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteImage();
                            }}
                            disabled={deletingImage}
                            data-testid="delete-campaign-image-btn"
                          >
                            {deletingImage ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4 mr-2" />
                            )}
                            Delete Image
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <ImageIcon className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground">{t('clickToUploadImage')}</p>
                      </>
                    )}
                  </div>
                </div>

                {/* Video URL */}
                <div>
                  <Label htmlFor="video_url">{t('videoUrlOptional')}</Label>
                  <div className="relative mt-2">
                    <Video className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      id="video_url"
                      placeholder="https://youtube.com/watch?v=..."
                      value={formData.video_url}
                      onChange={(e) => setFormData(prev => ({ ...prev, video_url: e.target.value }))}
                      className="pl-10 bg-secondary"
                    />
                  </div>
                </div>

                {/* Team Fundraising Toggle */}
                <div className="border border-border rounded-xl p-4 bg-secondary/30">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                        <Users className="w-5 h-5 text-emerald-400" />
                      </div>
                      <div>
                        <Label className="text-base font-medium">Team Fundraising</Label>
                        <p className="text-sm text-muted-foreground">
                          Allow supporters to create their own fundraising pages for your campaign
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {togglingTeam && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
                      <Switch 
                        data-testid="team-fundraising-toggle-switch"
                        checked={formData.team_fundraising_enabled}
                        onCheckedChange={handleToggleTeamFundraising}
                        disabled={togglingTeam}
                      />
                    </div>
                  </div>
                  {formData.team_fundraising_enabled && (
                    <div className="mt-3 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                      <p className="text-sm text-emerald-400">
                        ✓ Team fundraising is enabled. Manage team pages from your Dashboard → Team Pages tab.
                      </p>
                    </div>
                  )}
                </div>

                {/* Submit */}
                <div className="flex gap-4">
                  <Button 
                    type="submit" 
                    disabled={saving}
                    className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:opacity-90"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('savingChanges')}
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" /> {t('saveChangesBtn')}
                      </>
                    )}
                  </Button>
                  <Link to={`/campaign/${id}`}>
                    <Button type="button" variant="outline">{t('cancel')}</Button>
                  </Link>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default EditCampaignPage;
